import { useUserInfo } from "@/api/user";
import { AccountPopover } from "@/components/AccountPopover/AccountPopover";
import { useOnboardingContext } from "@/context/OnboardingContext";
import { useToggleTheme } from "@/hooks/useToggleTheme";
import { cn } from "@/utils";
import { PersonAvatar, useThemeContext } from "@cohesive/ui-components";
import { AnimatePresence, motion } from "framer-motion";
import { Moon, Sun } from "lucide-react";
import React, { useEffect, useMemo } from "react";
import { Outlet } from "react-router-dom";

/** Fixed top-right account trigger; must forward ref for PopoverTrigger asChild. */
const OnboardingAccountAvatarTrigger = React.forwardRef<
  HTMLButtonElement,
  React.ComponentPropsWithoutRef<"button">
>(function OnboardingAccountAvatarTrigger({ className, ...props }, ref) {
  const { data: userInfo } = useUserInfo();
  const user = userInfo?.user;

  return (
    <button
      ref={ref}
      type="button"
      data-testid="onboarding-account-avatar"
      aria-label="Account menu"
      title={user?.email ?? user?.name}
      className={cn(
        "h-9 w-9 md:h-10 md:w-10 rounded-full bg-background/90 backdrop-blur-md border shadow-lg hover:bg-accent transition-colors flex items-center justify-center overflow-hidden p-0",
        className,
      )}
      {...props}
    >
      <PersonAvatar
        name={user?.name}
        email={user?.email}
        pictureUrl={user?.picture_url}
        className="h-full w-full text-xs md:text-sm"
      />
    </button>
  );
});

export const OnboardingLayout: React.FC = React.memo(() => {
  const { currentStep, wizardSteps, currentWizardStepIndex, showProgress } =
    useOnboardingContext();
  const { mode } = useThemeContext();
  const { toggleTheme } = useToggleTheme();

  // Calculate total steps to display
  const totalSteps = useMemo(() => {
    const baseSteps = [1, 2, 3, 4];
    // If on step 4 and we have wizard steps, add them as additional steps
    if (currentStep === 4 && wizardSteps.length > 0) {
      return [...baseSteps, ...wizardSteps.map((_, idx) => 4 + idx + 1)];
    }
    return baseSteps;
  }, [currentStep, wizardSteps]);

  // Calculate which step indicator should be highlighted
  const activeStepIndex = useMemo(() => {
    if (currentStep < 4) {
      return currentStep - 1; // 0-indexed
    }
    // On step 4 with wizard steps
    if (wizardSteps.length > 0) {
      return 3 + currentWizardStepIndex + 1; // Base 4 steps + current wizard step
    }
    return 3; // Step 4 (0-indexed)
  }, [currentStep, wizardSteps.length, currentWizardStepIndex]);

  useEffect(() => {
    // Remove the forced light mode - let theme context handle it
    document.body.classList.remove("login-bg");

    return () => {
      document.body.classList.remove("login-bg");
    };
  }, []);

  return (
    <div className="min-h-dvh w-full bg-background relative overflow-hidden flex align-items-center justify-center">
      <title>Get Started</title>

      {/* Main content - full height with bottom padding on mobile to prevent overlap */}
      <div className="relative z-10 w-full flex items-center justify-center">
        <Outlet />
      </div>

      {/* Progress indicator - floating over content at bottom */}
      <div className="fixed bottom-3 md:bottom-8 left-1/2 -translate-x-1/2 z-50 pointer-events-none">
        <AnimatePresence>
          {showProgress && (
            <motion.div
              className="flex items-center p-2 md:p-4 bg-background/90 backdrop-blur-md border rounded-full shadow-lg"
              initial={{ opacity: 0, y: 20, scale: 0.9 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 20, scale: 0.9 }}
              layout
              transition={{
                duration: 0.3,
                ease: "easeInOut",
                layout: { duration: 0.3, ease: "easeInOut" },
              }}
            >
              <AnimatePresence>
                {totalSteps.map((step, index) => (
                  <motion.div
                    key={step}
                    initial={{ width: 0, opacity: 0, marginRight: 0 }}
                    animate={{
                      width:
                        index === activeStepIndex
                          ? "2rem"
                          : index < activeStepIndex
                            ? ".5rem"
                            : ".5rem",
                      opacity: 1,
                      marginRight:
                        index === totalSteps.length - 1 ? 0 : ".75rem",
                    }}
                    exit={{ width: 0, opacity: 0, marginRight: 0 }}
                    transition={{
                      duration: 0.3,
                      ease: "easeInOut",
                    }}
                    className={cn(
                      "h-1.5 md:h-2 rounded-full transition-colors duration-500",
                      index === activeStepIndex
                        ? "bg-foreground"
                        : index < activeStepIndex
                          ? "bg-foreground/80"
                          : "bg-border",
                    )}
                  />
                ))}
              </AnimatePresence>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Theme toggle - top left on mobile, bottom left on desktop */}
      <div className="fixed top-4 md:top-auto md:bottom-8 left-4 md:left-7 z-50">
        <button
          onClick={toggleTheme}
          className="h-9 w-9 md:h-10 md:w-10 rounded-full bg-background/90 backdrop-blur-md border shadow-lg hover:bg-accent transition-colors flex items-center justify-center"
          aria-label={`Switch to ${mode === "light" ? "dark" : "light"} mode`}
        >
          {mode === "dark" ? (
            <Sun className="h-4 w-4 md:h-5 md:w-5 text-foreground" />
          ) : (
            <Moon className="h-4 w-4 md:h-5 md:w-5 text-foreground" />
          )}
        </button>
      </div>

      {/* Account menu - fixed top-right on mobile and desktop */}
      <div className="fixed top-4 right-4 md:right-7 z-50">
        <AccountPopover
          popoverSide="bottom"
          popoverAlign="end"
          showAppOnlyActions={false}
        >
          <OnboardingAccountAvatarTrigger />
        </AccountPopover>
      </div>
    </div>
  );
});
